/**
 * plainPreview - flatten a markdown message down to one line of plain text.
 *
 * The conversation list shows the last message under each title. That message
 * is markdown, because everything the copilot says is markdown, and the list
 * was printing it raw: a preview reading
 *
 *   "Here are **LaShonda's** 3 open cases: | Case ID | Status |"
 *
 * shows the reader the markup instead of the sentence. MarkdownText is the
 * wrong fix here - a two-line clamped preview is not the place for tables,
 * headings and rules, and rendering them there would make list rows different
 * heights for no gain. So the markup is removed rather than honoured.
 *
 * Deliberately lossy: a table becomes its cell text, a code fence becomes its
 * code. A preview exists to help someone recognise a conversation, not to
 * reproduce it.
 */
export function plainPreview(text: string): string {
  if (!text) return text;
  return (
    text
      // Fenced code: keep the code, drop the fence and its language tag.
      .replace(/```[a-z]*\n?([\s\S]*?)```/gi, "$1")
      // Table separator rows carry no words at all.
      .replace(/^\|[ \t]*:?-+:?[ \t]*(?:\|[ \t]*:?-+:?[ \t]*)*\|[ \t]*$/gm, "")
      // Remaining pipes become separators the eye can read.
      .replace(/^\|(.+)\|[ \t]*$/gm, (_m, row: string) =>
        row
          .split("|")
          .map((c) => c.trim())
          .filter(Boolean)
          .join(" · "),
      )
      // The line-anchored rules above miss a truncated table: the server cuts
      // the preview mid-row, so the separator arrives as "|---|---|---|--"
      // with no closing pipe and no newline after it. Anchorless, but it must
      // start at a pipe or it would eat an em-dash out of prose.
      .replace(/\|(?:[ \t]*:?-+:?[ \t]*\|?)+/g, " ")
      // Whatever pipes survive were cell walls; give them a readable divider.
      .replace(/[ \t]*\|[ \t]*/g, " · ")
      .replace(/^#{1,6}[ \t]+/gm, "")
      .replace(/^-{3,}[ \t]*$/gm, "")
      .replace(/^[ \t]*[*-][ \t]+/gm, "")
      .replace(/^[ \t]*\d+\.[ \t]+/gm, "")
      .replace(/\*\*(.+?)\*\*/g, "$1")
      .replace(/__(.+?)__/g, "$1")
      .replace(/(?<!\w)\*([^*]+)\*(?!\w)/g, "$1")
      .replace(/`([^`]+)`/g, "$1")
      // Links: the label is the readable half.
      .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
      .replace(/\s+/g, " ")
      .trim()
  );
}
